import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import axios from 'axios'
import RegistrationRequestsModal from '../user/RegistrationRequestsModal'
import '../../styles/user/user-layout.scss'

// Create a separate axios instance WITHOUT interceptors for notifications
const notificationAxios = axios.create({
  baseURL: '/api',
  timeout: 5000,
})

const UserLayout = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { tenant, siteSettings } = useTenant()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState({})
  const [unreadCount, setUnreadCount] = useState(0)
  const [showRegRequests, setShowRegRequests] = useState(false)

  // Get logged-in user data
  const user = JSON.parse(localStorage.getItem('tenant_admin_user') || '{}')

  // Sidebar title for the signed-in unit. The organisation's own name comes
  // from the tenant, so the unit is named on its own rather than under a
  // hardcoded federation title that does not apply to every tenant.
  const getDisplayName = () => {
    if (user?.association_name) return user.association_name.toUpperCase()
    if (user?.city) return user.city.toUpperCase()
    if (user?.first_name) return `${user.first_name} ${user.last_name || ''}`.trim()
    return tenant?.name || 'Sports Portal'
  }

  // Fetch unread notification count
  useEffect(() => {
    // Fetch once on mount
    const timer = setTimeout(() => {
      fetchUnreadCount()
    }, 1000) // Wait 1 second after login

    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchUnreadCount, 60000)

    return () => {
      clearTimeout(timer)
      clearInterval(interval)
    }
  }, [])

  // Re-fetch unread count on every page navigation
  useEffect(() => {
    fetchUnreadCount()
  }, [location.pathname])

  // Listen for instant badge update when notification is read
  useEffect(() => {
    const handleUpdate = () => fetchUnreadCount()
    window.addEventListener('notifications-updated', handleUpdate)
    return () => window.removeEventListener('notifications-updated', handleUpdate)
  }, [])

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('tenant_admin_token')
      if (!token) return // Don't fetch if no token

      // Use separate axios instance to avoid logout interceptor
      const response = await notificationAxios.get('/user/notifications/unread-count', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      }).catch(() => null)

      if (response?.data?.unread_count !== undefined) {
        setUnreadCount(response.data.unread_count)
      }
    } catch (error) {
      // Silently fail
    }
  }

  const menuItems = [
    {
      path: '/user/dashboard',
      icon: 'fas fa-home',
      label: 'Dashboard',
      badge: null
    },
    {
      path: '/user/my-players',
      icon: 'fas fa-users',
      label: 'My Team Members',
      badge: null
    },
    {
      icon: 'fas fa-calendar-alt',
      label: 'Tournament',
      badge: null,
      submenu: [
        {
          path: '/user/my-events',
          icon: 'fas fa-user-check',
          label: 'National Tournament'
        },
        {
          path: '/user/fixtures',
          icon: 'fas fa-stopwatch',
          label: 'Match Schedule'
        },
        {
          path: '/user/tournament-records',
          icon: 'fas fa-trophy',
          label: 'State Tournament'
        }
      ]
    },
    {
      path: '/user/scorers',
      icon: 'fas fa-user-edit',
      label: 'Match Scorers',
      badge: null
    },
    {
      path: '/user/certificates',
      icon: 'fas fa-certificate',
      label: 'Certificates',
      badge: null
    },
    {
      path: '/user/notifications',
      icon: 'fas fa-bell',
      label: 'Notifications',
      badge: unreadCount > 0 ? unreadCount : null // Only show when count > 0
    },
    {
      path: '/user/profile',
      icon: 'fas fa-user-circle',
      label: 'My Profile',
      badge: null
    },
    {
      path: '/user/settings',
      icon: 'fas fa-cog',
      label: 'Settings',
      badge: null
    }
  ]

  // Auto-expand submenu if current route matches a submenu item
  useEffect(() => {
    menuItems.forEach((item, index) => {
      if (item.submenu) {
        const hasActiveChild = item.submenu.some(sub => isActive(sub.path))
        if (hasActiveChild) {
          setExpandedMenus(prev => ({ ...prev, [index]: true }))
        }
      }
    })
  }, [location.pathname])

  const toggleSubmenu = (index) => {
    setExpandedMenus(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const handleLogout = async () => {
    try {
      localStorage.removeItem('tenant_admin_token')
      localStorage.removeItem('tenant_admin_user')
      navigate('/user-login')
    } catch (err) {
      console.error('Logout error:', err)
    }
  }

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Get theme colors based on tenant
  const getThemeColors = () => {
    const theme = tenant?.settings?.theme || tenant?.theme || 'default'

    if (theme === 'throwball' || tenant?.slug === 'tfi') {
      return {
        primary: '#c00f15',
        primaryDark: '#a00d12',
        gradient: '#ffffff',
        accent: '#c00f15',
        textColor: '#c00f15',
        menuTextColor: '#4b5563',
        menuActiveColor: '#c00f15',
        menuActiveBg: '#fef2f2',
        isWhiteTheme: true
      }
    }
    // Default ABFI theme (blue)
    return {
      primary: '#022a5e',
      primaryDark: '#053e88',
      gradient: 'linear-gradient(180deg, #022a5e 0%, #053e88 100%)',
      accent: '#fbbf24',
      textColor: '#ffffff',
      menuTextColor: 'rgba(255, 255, 255, 0.9)',
      menuActiveColor: '#ffffff',
      menuActiveBg: 'rgba(255, 255, 255, 0.15)',
      isWhiteTheme: false
    }
  }

  const themeColors = getThemeColors()

  // Dynamic styles for sidebar
  const sidebarStyle = {
    background: themeColors.gradient,
    borderRight: themeColors.isWhiteTheme ? '1px solid #e5e7eb' : 'none',
    boxShadow: themeColors.isWhiteTheme ? '2px 0 10px rgba(0, 0, 0, 0.08)' : '4px 0 20px rgba(0, 0, 0, 0.1)'
  }

  const brandIconStyle = {
    color: themeColors.accent
  }

  const brandTextStyle = {
    color: themeColors.textColor
  }

  const activeIndicatorColor = themeColors.accent

  return (
    <div className={`user-layout ${themeColors.isWhiteTheme ? 'white-theme-layout' : ''}`} style={{ '--accent-color': themeColors.accent, '--primary-color': themeColors.primary, '--menu-text-color': themeColors.menuTextColor, '--menu-active-color': themeColors.menuActiveColor, '--menu-active-bg': themeColors.menuActiveBg, '--text-color': themeColors.textColor }}>
      {/* Sidebar */}
      <aside className={`user-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${themeColors.isWhiteTheme ? 'white-theme' : ''}`} style={sidebarStyle}>
        {/* Logo/Brand */}
        <div className="sidebar-header" style={{ borderBottom: themeColors.isWhiteTheme ? '1px solid #e5e7eb' : '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="brand">
            <img
              src={siteSettings?.logo || '/theme/assets/images/logo.png'}
              alt={user?.association_name || tenant?.name || 'Logo'}
              style={{ width: '32px', height: '32px', objectFit: 'contain', flexShrink: 0 }}
            />
            {!sidebarCollapsed && <span className="brand-text" style={brandTextStyle}>{getDisplayName()}</span>}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'angle-right' : 'angle-left'}`}></i>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="sidebar-nav">
          <ul className="menu-list">
            {menuItems.map((item, index) => (
              <li key={item.path || index} className={item.submenu ? 'has-submenu' : (item.path && isActive(item.path) ? 'active' : '')}>
                {item.submenu ? (
                  <>
                    <div
                      className={`menu-item ${expandedMenus[index] ? 'expanded' : ''}`}
                      onClick={() => toggleSubmenu(index)}
                      title={sidebarCollapsed ? item.label : ''}
                    >
                      <i className={item.icon}></i>
                      {!sidebarCollapsed && (
                        <>
                          <span className="menu-label">{item.label}</span>
                          <i className={`fas fa-chevron-${expandedMenus[index] ? 'down' : 'right'} submenu-arrow`}></i>
                        </>
                      )}
                    </div>
                    {!sidebarCollapsed && expandedMenus[index] && (
                      <ul className="submenu-list">
                        {item.submenu.map((subItem) => (
                          <li key={subItem.path} className={isActive(subItem.path) ? 'active' : ''}>
                            <Link to={subItem.path}>
                              <i className={subItem.icon}></i>
                              <span className="submenu-label">{subItem.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : item.onClick ? (
                  <a href="#" onClick={(e) => { e.preventDefault(); item.onClick(); }} title={sidebarCollapsed ? item.label : ''} style={{ position: 'relative', cursor: 'pointer' }}>
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                      <i className={item.icon}></i>
                    </span>
                    {!sidebarCollapsed && <span className="menu-label">{item.label}</span>}
                  </a>
                ) : (
                  <Link to={item.path} title={sidebarCollapsed ? item.label : ''} style={{ position: 'relative' }}>
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                      <i className={item.icon}></i>
                      {(item.badge !== null && item.badge !== undefined) && (
                        <span style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-8px',
                          backgroundColor: '#c00f15',
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: '700',
                          minWidth: '16px',
                          height: '16px',
                          borderRadius: '9px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          lineHeight: 1,
                          padding: '0 3px',
                          boxSizing: 'border-box'
                        }}>{item.badge}</span>
                      )}
                    </span>
                    {!sidebarCollapsed && <span className="menu-label">{item.label}</span>}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="sidebar-footer">
          <button
            className="logout-btn"
            onClick={handleLogout}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <i className="fas fa-sign-out-alt"></i>
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="user-main-content">
        {children}
      </main>

      {/* Registration Requests Modal */}
      {showRegRequests && (
        <RegistrationRequestsModal onClose={() => setShowRegRequests(false)} />
      )}
    </div>
  )
}

export default UserLayout
